import React from "react"
import PropTypes from "prop-types"
import { Flex } from "antd"
import { useTranslation } from "react-i18next"
import { numberToCurrency } from "helpers/string"
import { getBookingSpanDays } from "utils/experienceAvailability"
import { formatDurationFields } from "utils/experience"
import { EXPERIENCE_TYPES } from "constants/experiences"
import { MIN_PRICE_SAVING } from "../constants"
import { getGuestLineLabel, splitPriceBreakdown } from "../helpers/priceLines"
import { PriceLine, StruckTotal } from "./OrderSummary"
import styles from "./BookingForm.module.css"

// "2 hours" / "90 minutes" for a single-day duration (trip_duration is in minutes; a ticketed
// experience has one length of its own), "3 days" for a multi-day booking.
const getDurationLabel = (experience, bookingSelections, t) => {
  if (experience?.duration_type === "multi_day") {
    return t("booking_widget.experience.duration_days", { count: getBookingSpanDays(experience, bookingSelections) })
  }
  if (experience?.experience_type === EXPERIENCE_TYPES.ticketed) return formatDurationFields(experience, t)
  const minutes = Number(bookingSelections?.tripLength?.price?.trip_duration) || 0
  if (!minutes) return null
  return minutes % 60 === 0 ? t("experiences.terms.hours", { count: minutes / 60 }) : t("experiences.terms.minutes", { count: minutes })
}

// New Booking's step 1 summary (redesign): what the selection costs, for the guide to check before
// continuing. Read from the price step 1 already calculates (bookingSelections.breakdown), so it
// matches step 2. Fees the guide lists on their own line (show_as_line_item) get their own row,
// as in the guest widget; the rest add up to "Taxes & Fees". The card processing fee depends on the
// payment method chosen in step 2, so the total is the price before it.
const BookingSummary = ({ experience, bookingSelections }) => {
  const { t } = useTranslation()
  const { guestLines, listedFees, otherFeesTotal } = splitPriceBreakdown(bookingSelections?.breakdown ?? [])
  const addOnsTotal = Number(bookingSelections?.addOnsTotal) || 0
  const subtotal = (Number(bookingSelections?.baseAmount) || 0) + addOnsTotal
  const total = Number(bookingSelections?.preCardTotal ?? bookingSelections?.amount) || 0
  const totalBeforeAdjustments = Number(bookingSelections?.preCardTotalBeforeAdjustments) || 0
  const durationLabel = getDurationLabel(experience, bookingSelections, t)

  return (
    <Flex vertical gap="var(--spacing-xs)" className={styles.summaryBox}>
      <div className={styles.summaryTitle}>
        {durationLabel ? t("booking.summary.title_with_duration", { duration: durationLabel }) : t("booking.summary.title")}
      </div>
      <div>
        <div className={styles.summaryItems}>
          {guestLines.map((item) => (
            <PriceLine key={item.label} label={getGuestLineLabel(item, t)} amount={item.amount} />
          ))}
          {addOnsTotal > 0 && <PriceLine label={t("booking.add_ons.title")} amount={numberToCurrency(addOnsTotal)} />}
        </div>
        {/* The price before taxes and fees (QA Ticketing #43). */}
        <Flex justify="space-between" align="baseline" gap="var(--spacing-m)" className={`${styles.summaryLine} ${styles.summarySubtotal}`}>
          <span>{t("booking.summary.subtotal")}</span>
          <span className={styles.summaryAmount}>{numberToCurrency(subtotal)}</span>
        </Flex>
        {listedFees.map((item) => (
          <PriceLine key={item.label} label={item.label} amount={item.amount} />
        ))}
        {otherFeesTotal > MIN_PRICE_SAVING && (
          <PriceLine label={t("booking.summary.taxes_and_fees")} amount={numberToCurrency(otherFeesTotal)} />
        )}
      </div>
      <Flex justify="space-between" align="baseline" gap="var(--spacing-m)" className={styles.summaryTotal}>
        <span>{t("booking.summary.total")}</span>
        <StruckTotal total={total} totalBeforeAdjustments={totalBeforeAdjustments} />
      </Flex>
    </Flex>
  )
}

BookingSummary.propTypes = {
  experience: PropTypes.shape({ duration_type: PropTypes.string, duration_mode: PropTypes.string, experience_type: PropTypes.string }),
  bookingSelections: PropTypes.shape({
    breakdown: PropTypes.array,
    addOnsTotal: PropTypes.number,
    baseAmount: PropTypes.number,
    amount: PropTypes.number,
    preCardTotal: PropTypes.number,
    preCardTotalBeforeAdjustments: PropTypes.number
  }).isRequired
}

BookingSummary.defaultProps = {
  experience: null
}

export default BookingSummary
